import React from 'react'
import {useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../_dashboard/layout/core'
import {isNotEmpty, KTCard, KTCardBody, QUERIES} from '../../../../_dashboard/helpers'
import 'react-quill/dist/quill.snow.css'
import {useQuery} from 'react-query'
import { getOneById } from '../../../services/common_requests'
import {useListView} from '../reservation-list/core/ListViewProvider'
import {useIntl} from 'react-intl'
import {ToastContainer} from 'react-toastify'
import { DetailPageBack } from '../../../../_dashboard/partials/components/DetailPageBack'

export interface DeleteResponse {
  payload?: {
    code?: number
  }
}

const ReservationDetailsPage: React.FC = () => {
  const {id} = useParams<{id: any}>()
  
  // Styles - Using CSS classes for theme compatibility
  const sectionTitleStyle = {
    fontFamily: 'Noto Sans, sans-serif',
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: '100%',
    letterSpacing: '0%',
    marginBottom: '16px',
  }

  const labelStyle = {
    fontFamily: 'Noto Sans, sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    marginBottom: '4px',
  }

  const valueStyle = {
    fontFamily: 'Noto Sans, sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    marginBottom: '12px',
  }

  const badgeStyle = (bgColor: string, textColor: string = '#FFFFFF') => ({
    backgroundColor: bgColor,
    color: textColor,
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    display: 'inline-block',
  })

  const reservationBreadcrumbs: Array<PageLink> = [
    {
      title: 'Reservation Management',
      path: '/apps/reservation-management/reservations',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ]

  const intl = useIntl()
  const {itemIdForUpdate, setItemIdForUpdate} = useListView()
  const enabledQuery: boolean = isNotEmpty(id)
  
  const {data: reservation} = useQuery(
    `${QUERIES.RESERVATIONS_LIST}-reservation-${id}`,
    () => {
      if (!id) {
        throw new Error('ID is undefined')
      }
      return getOneById(id, '/reservations')
    },
    {
      cacheTime: 0,
      enabled: enabledQuery,
      onError: (err) => {
        setItemIdForUpdate(undefined)
        console.error(err)
      },
    }
  )

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const renderInfoItem = (label: string, value: string | number | undefined, customStyle?: any) => (
    <div className='mb-4'>
      <div className='text-muted fw-semibold fs-7 mb-1' style={labelStyle}>{label}</div>
      <div className='text-gray-800 fw-bold fs-6' style={customStyle || valueStyle}>{value || 'N/A'}</div>
    </div>
  )

  const isErrorStatus = () => {
    const statusCode = reservation?.status?.code?.toString()
    return (
      !!reservation?.errorMessage ||
      ['7', '10', '11', '2', '8'].includes(statusCode || '')
    )
  }

  const renderErrorInfo = () => {
    if (!isErrorStatus()) return null

    return (
      <div className='col-12 mb-5'>
        <KTCard>
          <KTCardBody>
            <h4 className='text-gray-800 fw-bold fs-4 mb-4' style={sectionTitleStyle}>⚠️ Erreur de réservation</h4>
            <div className='separator mb-4'></div>
            {renderInfoItem('Message d\'erreur', reservation?.errorMessage)}
            {renderInfoItem('Code d\'erreur', reservation?.errorCode)}
            {renderInfoItem('ID Log', reservation?.idLog)}
          </KTCardBody>
        </KTCard>
      </div>
    )
  }

  const renderParticipants = () => {
    if (!reservation?.bookingData?.Rooms?.length) return null

    return reservation.bookingData.Rooms.map((room: any, roomIndex: number) => (
      <div key={roomIndex} className='mb-4 p-4 border rounded-lg bg-light-dark'>
        <h6 className='text-gray-800 fw-bold mb-3'>Chambre {roomIndex + 1}</h6>
        {room.Pax?.Adult?.map((participant: any, participantIndex: number) => (
          <div key={participantIndex} className='d-flex align-items-center mb-3 p-3 bg-body rounded'>
            <div className='me-3'>
              <div className='symbol symbol-circle symbol-50px'>
                <div className='symbol-label bg-primary text-white fw-bold'>
                  {participant.Name?.charAt(0)}{participant.Surname?.charAt(0)}
                </div>
              </div>
            </div>
            <div className='flex-grow-1'>
              <div className='d-flex align-items-center mb-1'>
                <span className='text-gray-800 fw-bold me-2'>{participant.Civility} {participant.Name} {participant.Surname}</span>
                {participant.Holder && (
                  <span style={badgeStyle('#28a745')}>Titulaire</span>
                )}
              </div>
              <div className='text-muted small'>
                <div>📧 {participant.Email || 'Non spécifié'}</div>
                <div>📞 {participant.Phone || 'Non spécifié'}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    ))
  }

  const renderAgencyInfo = () => {
    if (!reservation?.agency || reservation?.paymentMethod?.code !== 'AGENCY') return null

    return (
      <div className='col-12 mb-5'>
        <KTCard>
          <KTCardBody>
            <h4 className='text-gray-800 fw-bold fs-4 mb-4' style={sectionTitleStyle}>🏢 Informations de l'agence</h4>
            <div className='separator mb-4'></div>
            
            <div className='d-flex align-items-start mb-4 p-4 bg-light-dark rounded'>
              <div className='symbol symbol-circle symbol-60px me-4'>
                <div className='symbol-label bg-light-success text-success fw-bold fs-4'>
                  🏢
                </div>
              </div>
              <div className='flex-grow-1'>
                <h5 className='text-gray-800 fw-bold mb-3'>{reservation.agency.name}</h5>
                <div className='row'>
                  <div className='col-md-6'>
                    {renderInfoItem('📍 Adresse', reservation.agency.address)}
                    {renderInfoItem('🏙️ Ville', reservation.agency.city)}
                  </div>
                  <div className='col-md-6'>
                    {renderInfoItem('📞 Téléphone', reservation.agency.phone)}
                    {renderInfoItem('👤 Personne de contact', reservation.agency.contactPerson)}
                  </div>
                </div>
                <div className='mt-3'>
                  <span style={badgeStyle(reservation.agency.isActive ? '#28a745' : '#dc3545')}>
                    {reservation.agency.isActive ? 'Agence active' : 'Agence inactive'}
                  </span>
                </div>
              </div>
            </div>
          </KTCardBody>
        </KTCard>
      </div>
    )
  }

  return (
    <>
      <ToastContainer />
      <PageTitle breadcrumbs={reservationBreadcrumbs}>
        {intl.formatMessage({
          id: 'ADMIN.COMMON.RESERVATION_DETAILS',
        })}
      </PageTitle>
      <DetailPageBack to='/apps/reservation-management/reservations' />

      {/* Header Card */}
      <KTCard className='mb-5'>
        <KTCardBody className='py-4'>
          <div className='d-flex justify-content-between align-items-center mb-4'>
            <h1 className='text-gray-800 fw-bold fs-2 mb-4' style={sectionTitleStyle}>Détails de la réservation</h1>
            {reservation?.downloadVoucherLink && (
              <a
                href={process.env.REACT_APP_API_URL_IMAGE + reservation.downloadVoucherLink}
                download
                className='btn btn-primary'
                style={{
                  borderRadius: '25px',
                  backgroundColor: '#C69E64',
                  border: 'none',
                  padding: '8px 20px',
                }}
              >
                📄 Télécharger le voucher
              </a>
            )}
          </div>

          <div className='d-flex align-items-start mb-4'>
            <img
              src={(process.env.REACT_APP_API_URL_IMAGE || '') + reservation?.hotel?.image?.url}
              alt={reservation?.hotel?.name}
              className='img-fluid me-4'
              style={{ 
                width: '200px', 
                height: '150px', 
                borderRadius: '12px',
                objectFit: 'cover'
              }}
            />
            <div className='flex-grow-1'>
              <h3 className='text-gray-800 fw-bold mb-3'>{reservation?.hotel?.name}</h3>
              <div className='row'>
                <div className='col-md-6'>
                  {renderInfoItem('Booking ID', reservation?.bookingId)}
                  {renderInfoItem('Hotel ID', reservation?.hotel?.hotel_id)}
                </div>
                <div className='col-md-6'>
                  {renderInfoItem('Numéro de commande', reservation?.orderNumber)}
                  {renderInfoItem('Numéro de voucher', reservation?.voucherNum)}
                </div>
              </div>
            </div>
          </div>
        </KTCardBody>
      </KTCard>

      <div className='row'>
        {/* Client Information */}
        <div className='col-md-6 mb-5'>
          <KTCard className='h-100'>
            <KTCardBody>
              <h4 className='text-gray-800 fw-bold fs-4 mb-4' style={sectionTitleStyle}>👤 Informations du client</h4>
              <div className='separator mb-4'></div>
              
              <div className='d-flex align-items-center mb-4'>
                <div className='symbol symbol-circle symbol-60px me-3'>
                  <div className='symbol-label bg-light-primary text-primary fw-bold fs-4'>
                    {reservation?.name?.charAt(0)}{reservation?.surname?.charAt(0)}
                  </div>
                </div>
                <div>
                  <div className='text-gray-800 fw-bold fs-5'>{reservation?.civility} {reservation?.name} {reservation?.surname}</div>
                  <div className='text-muted'>Client titulaire</div>
                </div>
              </div>

              {renderInfoItem('📧 Email', reservation?.email)}
              {renderInfoItem('📞 Téléphone', reservation?.phone)}
            </KTCardBody>
          </KTCard>
        </div>

        {/* Reservation Details */}
        <div className='col-md-6 mb-5'>
          <KTCard className='h-100'>
            <KTCardBody>
              <h4 className='text-gray-800 fw-bold fs-4 mb-4' style={sectionTitleStyle}>📅 Détails de la réservation</h4>
              <div className='separator mb-4'></div>
              
              {renderInfoItem('Date d\'arrivée', formatDate(reservation?.checkInDate || ''))}
              {renderInfoItem('Date de départ', formatDate(reservation?.checkOutDate || ''))}
              
              <div className='mb-4'>
                <div className='text-muted fw-semibold fs-7 mb-1' style={labelStyle}>Méthode de paiement</div>
                <span style={badgeStyle('#E8F1FD', '#305889')}>
                  {reservation?.paymentMethod?.description}
                </span>
              </div>

                             <div className='mb-4'>
                 <div className='text-muted fw-semibold fs-7 mb-1' style={labelStyle}>Statut</div>
                 <span style={badgeStyle(reservation?.status?.color || '#6c757d')}>
                   {reservation?.status?.name || 'En attente'}
                 </span>
               </div>
            </KTCardBody>
          </KTCard>
        </div>

                 {/* Participants */}
         <div className='col-12 mb-5'>
           <KTCard>
             <KTCardBody>
               <h4 className='text-gray-800 fw-bold fs-4 mb-4' style={sectionTitleStyle}>👥 Participants</h4>
               <div className='separator mb-4'></div>
               {renderParticipants()}
             </KTCardBody>
           </KTCard>
         </div>

         {/* Agency Information - Only shown when payment method is AGENCY */}
         {renderAgencyInfo()}

         {renderErrorInfo()}

         {/* Financial Information */}
        <div className='col-md-6 mb-5'>
          <KTCard className='h-100'>
            <KTCardBody>
              <h4 className='text-gray-800 fw-bold fs-4 mb-4' style={sectionTitleStyle}>💰 Informations financières</h4>
              <div className='separator mb-4'></div>
              
              <div className='mb-4'>
                <div className='text-muted fw-semibold fs-7 mb-1' style={labelStyle}>Prix total</div>
                <div className='text-warning fw-bold fs-4' style={{...valueStyle, fontSize: '18px'}}>
                  {reservation?.totalPrice} TND
                </div>
              </div>

              <div className='mb-4'>
                <div className='text-muted fw-semibold fs-7 mb-1' style={labelStyle}>Montant à payer</div>
                <div className='text-warning fw-bold fs-4' style={{...valueStyle, fontSize: '18px'}}>
                  {reservation?.totalToPay} TND
                </div>
              </div>
            </KTCardBody>
          </KTCard>
        </div>

        {/* Technical Information */}
        <div className='col-md-6 mb-5'>
          <KTCard className='h-100'>
            <KTCardBody>
              <h4 className='text-gray-800 fw-bold fs-4 mb-4' style={sectionTitleStyle}>🔧 Informations techniques</h4>
              <div className='separator mb-4'></div>
              
              {renderInfoItem('ID Log', reservation?.idLog)}
              {renderInfoItem('Date de création', formatDateTime(reservation?.createdAt || ''))}
              {renderInfoItem('Dernière mise à jour', formatDateTime(reservation?.updatedAt || ''))}
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    </>
  )
}

export {ReservationDetailsPage}
